import type {
  ApiResponse,
  LoginResponse,
  Shipment,
  ShipmentItem,
  HTSClassification,
  AmazonFC
} from '../types'
import { requestObfuscation } from '../utils/requestObfuscation'
import { trustedDevice } from '../utils/trustedDevice'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:2999'

// Define a callback for handling authentication failures
let authFailureCallback: (() => void) | null = null

class ApiService {
  // Method to set the auth failure callback
  setAuthFailureCallback(callback: () => void) {
    authFailureCallback = callback
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`

    // Add obfuscated headers to ALL API requests for maximum security
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    // Add auth token if available
    const token = localStorage.getItem('yyz-cbp-token')
    if (token) {
      baseHeaders['Authorization'] = `Bearer ${token}`
    }

    const finalHeaders = requestObfuscation.generateRequestHeaders(baseHeaders)

    const config: RequestInit = {
      ...options,
      // Preserve request-specific options without letting options.headers replace
      // the Authorization and obfuscation headers assembled above.
      headers: finalHeaders,
    }

    try {
      const response = await fetch(url, config)
      const responseText = await response.text()
      let data: any = {}

      if (responseText) {
        try {
          data = JSON.parse(responseText)
        } catch {
          data = {
            success: false,
            error: response.ok
              ? 'Server returned an unreadable response.'
              : `Server returned ${response.status} ${response.statusText || 'error'} instead of JSON.`
          }
        }
      }

      // Deobfuscate response if it's obfuscated
      data = requestObfuscation.deobfuscateResponse(data)

      if (!response.ok) {
        // Handle authentication failures (401 Unauthorized)
        if (response.status === 401) {
          if (endpoint.includes('/api/auth/login')) {
            throw new Error(data.error || 'Login failed. Please check your email and try again.')
          }

          console.warn(`🔑 401 Unauthorized for ${endpoint}`)

          // Don't interfere with auth validation endpoints
          if (endpoint.includes('/api/client-business-info')) {
            console.log('📋 Auth validation endpoint failed, letting context handle it')
            throw new Error('Your session has expired. Please log in again.')
          }

          // Only destroy the local session when the backend explicitly says the
          // JWT itself is unusable. A protected feature can return 401 for its
          // own authorization rules without invalidating an otherwise valid login.
          const authError = String(data.error || '').toLowerCase()
          const invalidSession = [
            'authorization header required',
            'authentication required',
            'invalid token',
            'token expired',
            'token verification failed',
            'client account not found'
          ].some(message => authError.includes(message))

          if (invalidSession) {
            console.warn('🧹 Clearing invalid auth data')
            localStorage.removeItem('yyz-cbp-token')
            localStorage.removeItem('yyz-cbp-email')

            if (authFailureCallback) {
              authFailureCallback()
            }
          }

          throw new Error(invalidSession
            ? 'Your session has expired. Please log in again.'
            : data.error || 'You are not authorized to perform this action.')
        }

        const apiError: any = new Error(data.error || `HTTP error! status: ${response.status}`)
        apiError.responseData = data
        apiError.status = response.status
        throw apiError
      }

      return data
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error)
      if (error instanceof TypeError && /fetch/i.test(error.message)) {
        const isSSBAction = endpoint.includes('/api/submit-to-ssb') || endpoint.includes('/api/upload-to-catalogue')
        throw new Error(isSSBAction
          ? 'Connection to the customs backend failed during the ShipSavvy action. Please wait a few seconds and retry; if it repeats, ShipSavvy or the backend may be timing out.'
          : 'Connection to the customs backend failed. Please check your connection and retry.')
      }
      throw error
    }
  }

  // Generic HTTP methods
  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint)
  }

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    })
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    return this.request('/api/health')
  }

  // Authentication
  async login(email: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async getTrustedActionDeviceStatus(): Promise<ApiResponse<{ trusted: boolean; configured: boolean }>> {
    return this.request<{ trusted: boolean; configured: boolean }>('/api/trusted-action-device', {
      method: 'GET',
      headers: {
        'x-yyz-trusted-device-id': trustedDevice.getId()
      }
    })
  }

  // Shipment management
  async getShipments(page: number = 1): Promise<ApiResponse<Shipment[]>> {
    return this.request<Shipment[]>(`/api/shipments?page=${page}`)
  }

  // Get single shipment by ID (optimized - avoids fetching all shipments)
  async getShipment(shipmentId: string): Promise<ApiResponse<Shipment>> {
    return this.request<Shipment>(`/api/shipments/${shipmentId}`)
  }

  async getShipmentItems(shipmentId: string): Promise<ApiResponse<ShipmentItem[]>> {
    return this.request<ShipmentItem[]>(`/api/shipments/${shipmentId}/items`)
  }

  async updateShipmentItem(
    shipmentId: string,
    itemId: string,
    updates: Partial<ShipmentItem>
  ): Promise<ApiResponse<ShipmentItem>> {
    return this.request<ShipmentItem>(`/api/shipments/${shipmentId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async addManualShipmentItems(
    shipmentId: string,
    items: Array<{
      sku?: string;
      description: string;
      quantity: number;
      countryOfOrigin?: string;
      declaredValue?: number | null;
      currency?: 'CAD' | 'USD';
      htsCode?: string;
    }>
  ): Promise<ApiResponse<ShipmentItem[]>> {
    return this.request<ShipmentItem[]>(`/api/shipments/${shipmentId}/manual-items`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  }

  // UNIFIED: Get complete shipment data in single request
  async getShipmentCompleteData(shipmentId: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/api/shipments/${shipmentId}/complete-data`)
  }

  async getClientASINHistory(asin: string, clientId?: string, currentShipmentId?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams()
    if (clientId) params.set('clientId', clientId)
    if (currentShipmentId) params.set('currentShipmentId', currentShipmentId)
    const query = params.toString() ? `?${params.toString()}` : ''
    return this.request<any>(`/api/client-asin-history/${encodeURIComponent(asin)}${query}`)
  }

  // Generate SSBC Excel for FDA compliance (uses tracking from YYZ Prep)
  async generateSSBC(shipmentId: string, password: string): Promise<Blob> {
    const url = `${API_BASE_URL}/api/shipments/${shipmentId}/generate-ssbc`
    const token = localStorage.getItem('yyz-cbp-token')

    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (token) {
      baseHeaders['Authorization'] = `Bearer ${token}`
    }

    // Add obfuscation headers (required for production backend)
    const headers = requestObfuscation.generateRequestHeaders(baseHeaders)

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ password })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to generate SSBC')
    }

    return response.blob()
  }

  // HTS code classification
  async classifyHTS(
    description: string,
    countryOfOrigin?: string,
    asin?: string,
    imageUrl?: string,
    forceRefresh?: boolean
  ): Promise<ApiResponse<HTSClassification>> {
    return this.request<HTSClassification>('/api/hts-classify', {
      method: 'POST',
      body: JSON.stringify({ description, countryOfOrigin, asin, imageUrl, forceRefresh }),
    })
  }

  async classifyHTSBatch(items: Array<{
    id: string
    sku?: string
    description: string
    countryOfOrigin?: string
    asin?: string
    imageUrl?: string
  }>, forceRefresh?: boolean): Promise<ApiResponse<any>> {
    return this.request<any>('/api/hts-classify/batch', {
      method: 'POST',
      body: JSON.stringify({ items, forceRefresh }),
    })
  }

  async classifyFDAProductCode(productTitle: string): Promise<ApiResponse<any>> {
    return this.request('/api/fda-product-code/classify', {
      method: 'POST',
      body: JSON.stringify({ productTitle }),
    })
  }

  async autoClassifyFDA(productTitle: string, htsCode?: string, htsDescription?: string): Promise<ApiResponse<{
    classification: 'FOOD' | 'COSMETIC' | 'NEITHER'
    isFood: boolean
    isCosmetic: boolean
    fdaCode: string | null
    fdaClassification: any | null
  }>> {
    return this.request('/api/auto-classify-fda', {
      method: 'POST',
      body: JSON.stringify({ productTitle, htsCode, htsDescription }),
    })
  }

  async suggestMetalComposition(data: {
    productTitle: string
    htsCode: string
    htsDescription?: string
    imageUrl?: string
  }): Promise<ApiResponse<{
    values: {
      metal_aluminum?: number
      metal_copper?: number
      metal_steel?: number
    }
    requiredFields: string[]
    confidence: 'high' | 'medium' | 'low'
    reasoning: string
  }>> {
    return this.request('/api/metal-composition-suggest', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async lookupHTSPGA(htsCode: string): Promise<ApiResponse<any>> {
    return this.request('/api/hts-pga-lookup', {
      method: 'POST',
      body: JSON.stringify({ htsCode }),
    })
  }

  // Amazon FBA
  async searchAmazonFC(search?: string): Promise<ApiResponse<AmazonFC[]>> {
    const params = search ? `?search=${encodeURIComponent(search)}` : ''
    return this.request<AmazonFC[]>(`/api/amazon-fc${params}`)
  }

  // Commercial invoice generation
  async generateInvoice(
    shipment: Shipment,
    items: ShipmentItem[],
    password: string,
    ltlType: 'direct_amazon' | 'pattern_middle_mile',
    brokerType: 'clearit' | 'acb' | 'jas',
    fcInfo?: {
      fcId: string;
      fcAddress: {
        street: string;
        city: string;
        state: string;
        zip: string;
      }
    },
    manualItems?: any[]
  ): Promise<ApiResponse<any>> {
    const shipmentData = {
      id: shipment.id,
      reference: shipment.reference,
      items: items
    }

    return this.request<any>('/api/generate-invoice', {
      method: 'POST',
      body: JSON.stringify({
        shipmentData,
        password,
        ltlType,
        brokerType,
        fcInfo,
        manualItems
      }),
    })
  }

  // Combined commercial invoice generation for multiple shipments
  async generateCombinedInvoice(
    shipments: Shipment[],
    password: string,
    ltlType: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo',
    brokerType: 'clearit' | 'acb' | 'jas',
    fcInfo?: {
      fcId: string;
      fcAddress: {
        street: string;
        city: string;
        state: string;
        zip: string;
      }
    }
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/api/generate-combined-invoice', {
      method: 'POST',
      body: JSON.stringify({
        shipments,
        password,
        ltlType,
        brokerType,
        fcInfo
      }),
    })
  }

  // Generate merged estimate clearance for multiple shipments
  async generateMergedEstimateClearance(
    shipmentIds: string[]
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/api/merged-estimate-clearance', {
      method: 'POST',
      body: JSON.stringify({
        shipmentIds
      }),
    })
  }

  // FDA Worksheet generation
  async generateFDAWorksheets(
    shipmentData: any,
    password: string
  ): Promise<ApiResponse<any>> {
    return this.request<any>('/api/generate-fda-worksheets', {
      method: 'POST',
      body: JSON.stringify({
        shipmentData,
        password
      }),
    })
  }

  // Submit shipment to SSB
  async submitToSSB(
    shipmentId: number,
    password: string,
    items: any[],
    skipTracking: boolean = false,
    onProgress?: (message: string) => void
  ): Promise<ApiResponse<any>> {
    const storageKey = `ssb-submission-job:${shipmentId}`
    let jobId = sessionStorage.getItem(storageKey)
    if (!jobId) {
      const started: any = await this.request<any>('/api/submit-to-ssb', {
        method: 'POST',
        body: JSON.stringify({
          shipmentId, password, trustedDeviceId: trustedDevice.getId(),
          items, skipTracking, backgroundSubmission: true
        }),
      })
      // Compatibility with a backend that has not yet received this update.
      if (!started.jobId) return started
      jobId = started.jobId
      sessionStorage.setItem(storageKey, jobId!)
    }
    let consecutiveFailures = 0
    while (true) {
      await new Promise(resolve => setTimeout(resolve, 3000))
      let status: any
      try {
        status = await this.request<any>(`/api/ssb-submission-jobs/${encodeURIComponent(jobId!)}`,
          { signal: AbortSignal.timeout(30000) })
        consecutiveFailures = 0
      } catch (error: any) {
        if (error.status === 401 || error.status === 403 || error.status === 404) throw error
        if (++consecutiveFailures < 10) continue
        throw new Error('Cannot read submission progress. The shipment may still be processing. Clicking Submit again will reconnect to the same job; do not start a new shipment.')
      }
      if (status.progress) onProgress?.(status.progress)
      if (status.state === 'completed') {
        sessionStorage.removeItem(storageKey)
        if (status.statusCode >= 400) {
          const error: any = new Error(status.result?.error || 'ShipSavvy submission failed')
          error.responseData = status.result
          error.status = status.statusCode
          throw error
        }
        return status.result
      }
    }
  }

  async getSSBHTSAudit(shipmentId: number): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/api/shipments/${shipmentId}/ssb-hts-audit`)
  }

  // Upload FDA/food/cosmetic products to SSB catalogue
  async uploadToCatalogue(
    shipmentId: string,
    password: string,
    items: any[]
  ): Promise<ApiResponse<{
    uploaded: number
    skipped: number
    failed: number
    total: number
    results: Array<{
      asin: string
      status: 'uploaded' | 'skipped' | 'failed'
      reason?: string
      error?: string
    }>
  }>> {
    return this.request('/api/upload-to-catalogue', {
      method: 'POST',
      body: JSON.stringify({
        shipmentId,
        password,
        trustedDeviceId: trustedDevice.getId(),
        items
      }),
    })
  }

  // Check if products exist in SSB catalogue
  async checkCatalogue(asins: string[]): Promise<ApiResponse<{
    existing: number
    missing: number
    results: Array<{
      asin: string
      exists: boolean
      product?: any
    }>
  }>> {
    return this.request('/api/check-catalogue', {
      method: 'POST',
      body: JSON.stringify({ asins }),
    })
  }

  // Get service breakdown by box weight
  async getServiceBreakdown(shipmentId: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/api/shipments/${shipmentId}/service-breakdown`, {
      method: 'GET'
    })
  }

  // Add services to shipment
  async addServicesToShipment(shipmentId: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/api/shipments/${shipmentId}/add-services`, {
      method: 'POST'
    })
  }

  // Download invoice PDF
  async downloadInvoice(filename: string): Promise<void> {
    const url = `${API_BASE_URL}/api/download-invoice/${filename}`

    const token = localStorage.getItem('yyz-cbp-token')
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json'
    }
    if (token) {
      baseHeaders['Authorization'] = `Bearer ${token}`
    }

    // Add obfuscation headers
    const headers = requestObfuscation.generateRequestHeaders(baseHeaders)

    try {
      const response = await fetch(url, { headers })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`)
      }

      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)

      // Create temporary download link
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Cleanup
      document.body.removeChild(link)
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 60000)

    } catch (error) {
      console.error('Download error:', error)
      throw error
    }
  }

  // Client-specific ASIN data
  async getClientASINData(asin: string, clientId?: string): Promise<ApiResponse<any>> {
    const encodedASIN = encodeURIComponent(asin)
    // If clientId provided (for staff), add as query param
    const url = clientId
      ? `/api/client-asin-data/${encodedASIN}?clientId=${clientId}`
      : `/api/client-asin-data/${encodedASIN}`
    return this.request(url)
  }

  async saveClientASINData(asin: string, data: any, clientId?: string): Promise<ApiResponse<any>> {
    const encodedASIN = encodeURIComponent(asin)
    const url = clientId
      ? `/api/client-asin-data/${encodedASIN}?clientId=${clientId}`
      : `/api/client-asin-data/${encodedASIN}`
    return this.request(url, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getHTSChangeEvents(asin: string, options: { clientId?: string; outboundId?: string; limit?: number } = {}): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams()
    if (options.clientId) params.set('clientId', options.clientId)
    if (options.outboundId) params.set('outboundId', options.outboundId)
    if (options.limit) params.set('limit', String(options.limit))
    const query = params.toString()
    return this.request(`/api/client-asin-data/${encodeURIComponent(asin)}/hts-events${query ? `?${query}` : ''}`)
  }

  async saveClientManufacturingAddress(address: any): Promise<ApiResponse<any>> {
    return this.request('/api/client-manufacturing-addresses', {
      method: 'POST',
      body: JSON.stringify(address),
    })
  }

  async updateClientManufacturingAddress(addressId: number, updates: any): Promise<ApiResponse<any>> {
    return this.request(`/api/client-manufacturing-addresses/${addressId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  }

  // ClearIt API
  async getClearItCountries(): Promise<ApiResponse<any[]>> {
    return this.request('/api/clearit/countries')
  }

  async classifyProduct(productTitle: string): Promise<ApiResponse<any>> {
    return this.request('/api/clearit/classify-product', {
      method: 'POST',
      body: JSON.stringify({ productTitle }),
    })
  }

  async estimateCustomsCosts(items: any[]): Promise<ApiResponse<any>> {
    return this.request('/api/clearit/estimate-costs', {
      method: 'POST',
      body: JSON.stringify({ items }),
    })
  }

  async lookupManufacturingAddress(productDescription: string, asin?: string, countryOfOrigin?: string): Promise<ApiResponse<any>> {
    // Using the ClearIt endpoint which now redirects to Gemini-based manufacturing service
    // Backend handles the transition transparently
    return this.request('/api/clearit/lookup-manufacturing-address', {
      method: 'POST',
      body: JSON.stringify({ productDescription, asin, countryOfOrigin }),
    })
  }

  async getCountrySuggestion(asin: string): Promise<ApiResponse<any>> {
    return this.request(`/api/country-suggestion/${asin}`)
  }

  // Client business information
  async getClientBusinessInfo(): Promise<ApiResponse<any>> {
    return this.request('/api/client-business-info')
  }

  async saveClientBusinessInfo(businessInfo: any): Promise<ApiResponse<any>> {
    return this.request('/api/client-business-info', {
      method: 'POST',
      body: JSON.stringify(businessInfo),
    })
  }

  async getExchangeRate(): Promise<ApiResponse<any>> {
    return this.request('/api/exchange-rate')
  }

  // eShipper API
  async getEShipperCountries(): Promise<ApiResponse<any>> {
    return this.request('/api/eshipper/countries')
  }

  async calculateItemDuty(data: {
    htsCode: string
    countryOfOrigin: string
    price: number
    description?: string
    sku?: string
  }): Promise<ApiResponse<any>> {
    return this.request('/api/eshipper/calculate-duty', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // SSB Rate Verification
  async checkSSBRate(data: {
    htsCode: string
    countryOfOrigin: string
    price: number
    description?: string
    asin?: string
    manufacturingAddress?: string
    metal_aluminum?: number | string | null
    metal_copper?: number | string | null
    metal_steel?: number | string | null
  }): Promise<ApiResponse<{
    dutyAmount: number
    dutyRate: string
    verified: boolean
    timestamp: string
  }>> {
    return this.request('/api/shipments/check-ssb-rate', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async getSSBRatesForItems(items: Array<{
    itemId: string
    htsCode: string
    countryOfOrigin: string
    price: number
    description?: string
    asin?: string
    manufacturingAddress?: string
    metal_aluminum?: number | string | null
    metal_copper?: number | string | null
    metal_steel?: number | string | null
  }>): Promise<ApiResponse<{
    results: Array<{
      itemId: string
      success: boolean
      dutyAmount?: number
      dutyAmountCAD?: number
      dutyAmountUSD?: number
      dutyRate?: string
      dutyRatePercent?: number
      verified?: boolean
      cached?: boolean
      source?: string
      currency?: string
      htsCode?: string
      countryOfOrigin?: string
      timestamp?: string
      cachedAt?: string | null
      cacheExpiresAt?: string
      error?: string
      usHtsValidation?: {
        checked: boolean
        current: boolean | null
        htsCode: string
        description?: string
        issue?: string | null
        effectiveDate?: string | null
        replacements?: Array<{ htsCode: string; description: string }>
        source?: string
        sourceUrl?: string
        checkedAt?: string
        userMessage?: string
      }
    }>
    summary: {
      total: number
      verified: number
      cached: number
      fetched: number
      failed: number
    }
  }>> {
    return this.request('/api/shipments/ssb-rates', {
      method: 'POST',
      body: JSON.stringify({ items })
    })
  }

  // Batch duty calculation - OPTIMIZED for multiple items
  async calculateItemDutyBatch(items: Array<{
    itemId: string
    htsCode: string
    countryOfOrigin: string
    price: number
    description?: string
    sku?: string
  }>): Promise<ApiResponse<{
    results: Array<{
      itemId: string
      success: boolean
      cached?: boolean
      dutyRatePercent?: number
      dutyAmount?: number
      error?: string
    }>
    summary: {
      total: number
      cached: number
      fetched: number
      failed: number
    }
  }>> {
    return this.request('/api/eshipper/calculate-duty-batch', {
      method: 'POST',
      body: JSON.stringify({ items })
    })
  }

  // HTS Code Validation
  async validateHTSCode(htsCode: string): Promise<ApiResponse<any>> {
    return this.request('/api/hts/validate', {
      method: 'POST',
      body: JSON.stringify({ htsCode })
    })
  }

  // AI Item Parsing for Manual Invoice Generation
  async parseItemsWithAI(itemText: string, invoiceDate?: string, shippingDate?: string): Promise<ApiResponse<any>> {
    return this.request('/api/ai-parse-items', {
      method: 'POST',
      body: JSON.stringify({
        itemText,
        invoiceDate,
        shippingDate
      }),
    })
  }

  // Generate manual one-time invoice
  async generateManualInvoice(invoiceData: any): Promise<ApiResponse<any>> {
    return this.request('/api/generate-manual-invoice', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    })
  }

  // Staff Manual Invoice Generation (no auth required)
  async generateStaffManualInvoice(invoiceData: any): Promise<ApiResponse<any>> {
    const url = `${API_BASE_URL}/api/staff/generate-manual-invoice`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invoiceData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('Staff invoice generation error:', error)
      throw error
    }
  }

  // Staff AI Item Parsing for Manual Invoice Generation (no auth required)
  async parseStaffItemsWithAI(itemText: string, invoiceDate?: string, shippingDate?: string): Promise<ApiResponse<any>> {
    const url = `${API_BASE_URL}/api/staff/ai-parse-items`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          itemText,
          invoiceDate,
          shippingDate
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('Staff AI parsing error:', error)
      throw error
    }
  }

  // Download staff invoice (no auth required)
  async downloadStaffInvoice(filename: string): Promise<void> {
    const url = `${API_BASE_URL}/api/staff/download-invoice/${filename}`
    console.log('📥 downloadStaffInvoice called with:', { filename, url })

    try {
      console.log('🌐 Fetching from URL:', url)
      // Fetch the file as a blob
      const response = await fetch(url, {
        method: 'GET',
      })

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      })

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`)
      }

      // Get the file as a blob
      console.log('🔄 Converting to blob...')
      const blob = await response.blob()
      console.log('✅ Blob created:', { size: blob.size, type: blob.type })

      // Create download link
      console.log('🔗 Creating download link...')
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)

      console.log('🖱️ Triggering download click...')
      link.click()

      // Cleanup
      console.log('🧹 Cleaning up...')
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      console.log('✅ Download process completed successfully')
    } catch (error) {
      console.error('❌ Error downloading staff invoice:', error)
      throw error
    }
  }

  // FreightCom cart integration
  async addToFreightcomCart(
    shipmentId: string,
    selectedRate: any,
    amazonFbaInfo: any,
    boxes: any[],
    customsData?: any
  ): Promise<ApiResponse<any>> {
    return this.request(`/api/shipments/${shipmentId}/freightcom-cart`, {
      method: 'POST',
      body: JSON.stringify({
        selectedRate,
        amazonFbaInfo,
        boxes,
        customsData
      })
    })
  }

  // CUSMA Certificate of Origin
  async checkCUSMAEligibility(shipmentId: string, items?: any[]): Promise<ApiResponse<any>> {
    const params = items ? `?items=${encodeURIComponent(JSON.stringify(items))}` : ''
    return this.request(`/api/shipments/${shipmentId}/cusma-eligibility${params}`)
  }

  // Staff CUSMA eligibility check
  async checkStaffCUSMAEligibility(clientId: string, shipmentId: string, items?: any[]): Promise<ApiResponse<any>> {
    const params = items ? `?items=${encodeURIComponent(JSON.stringify(items))}` : ''
    return this.request(`/api/staff/clients/${clientId}/shipments/${shipmentId}/cusma-eligibility${params}`)
  }

  // Staff CUSMA form management
  async getStaffCUSMAForm(clientId: string, shipmentId: string): Promise<ApiResponse<any>> {
    return this.request(`/api/staff/clients/${clientId}/shipments/${shipmentId}/cusma-form`)
  }

  async generateStaffCUSMAForm(clientId: string, shipmentId: string, items?: any[]): Promise<ApiResponse<any>> {
    return this.request(`/api/staff/clients/${clientId}/shipments/${shipmentId}/cusma-form`, {
      method: 'POST',
      body: items ? JSON.stringify({ items }) : undefined
    })
  }

  async updateStaffCUSMAForm(clientId: string, shipmentId: string, formId: string, formData: any): Promise<ApiResponse<any>> {
    return this.request(`/api/staff/clients/${clientId}/shipments/${shipmentId}/cusma-form/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(formData)
    })
  }

  async generateCUSMAForm(shipmentId: string, items?: any[]): Promise<ApiResponse<any>> {
    return this.request(`/api/shipments/${shipmentId}/cusma-form`, {
      method: 'POST',
      body: items ? JSON.stringify({ items }) : undefined
    })
  }

  async getCUSMAForm(shipmentId: string): Promise<ApiResponse<any>> {
    return this.request(`/api/shipments/${shipmentId}/cusma-form`)
  }

  async updateCUSMAForm(shipmentId: string, formId: string, formData: any): Promise<ApiResponse<any>> {
    return this.request(`/api/shipments/${shipmentId}/cusma-form/${formId}`, {
      method: 'PUT',
      body: JSON.stringify(formData)
    })
  }

  // Auto-save single field update for CUSMA form
  async updateCUSMAField(shipmentId: string, formId: string, field: string, value: any): Promise<ApiResponse<{ success: boolean, lastAutosaveAt?: string }>> {
    return this.request(`/api/shipments/${shipmentId}/cusma-form/${formId}`, {
      method: 'PUT',
      body: JSON.stringify({ field, value })
    })
  }

  // Auto-save batch field updates for CUSMA form
  async updateCUSMAFields(shipmentId: string, formId: string, updates: Array<{ field: string, value: any }>): Promise<ApiResponse<{ success: boolean, lastAutosaveAt?: string }>> {
    return this.request(`/api/shipments/${shipmentId}/cusma-form/${formId}`, {
      method: 'PUT',
      body: JSON.stringify({ updates })
    })
  }

  // S3 CUSMA Forms Management
  async listS3CUSMAForms(clientId?: string): Promise<ApiResponse<{ forms: any[], count: number }>> {
    const url = clientId ? `/api/s3/cusma-forms?clientId=${clientId}` : '/api/s3/cusma-forms'
    return this.request(url)
  }

  async downloadS3CUSMAForm(fileName: string, clientId?: string): Promise<Response> {
    const queryParam = clientId ? `?clientId=${clientId}` : ''
    const url = `${API_BASE_URL}/api/s3/cusma-forms/${fileName}${queryParam}`
    const token = localStorage.getItem('yyz-cbp-token')

    // Need to include obfuscation headers for production backend
    const baseHeaders: Record<string, string> = {
      'Authorization': token ? `Bearer ${token}` : '',
      // Don't set Content-Type to let browser handle binary
    }

    const headers = requestObfuscation.generateRequestHeaders(baseHeaders)

    return fetch(url, { headers })
  }

  async deleteS3CUSMAForm(fileName: string, clientId?: string): Promise<ApiResponse<any>> {
    const queryParam = clientId ? `?clientId=${clientId}` : ''
    return this.request(`/api/s3/cusma-forms/${fileName}${queryParam}`, {
      method: 'DELETE'
    })
  }

  async generateS3PresignedURL(fileName: string, operation: string = 'getObject', expiresIn: number = 3600): Promise<ApiResponse<{ url: string }>> {
    return this.request(`/api/s3/cusma-forms/${fileName}/presigned-url?operation=${operation}&expiresIn=${expiresIn}`)
  }

  // Debug: Get CUSMA template form fields
  async getCUSMATemplateFields(): Promise<ApiResponse<{ fields: any[], count: number }>> {
    return this.request('/api/cusma-template-fields')
  }

  // Debug: Download annotated CUSMA template PDF
  async downloadAnnotatedCUSMATemplate(): Promise<Response> {
    const url = `${API_BASE_URL}/api/cusma-template-annotated`
    const token = localStorage.getItem('yyz-cbp-token')

    return fetch(url, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
      }
    })
  }

  // ============================================
  // Multi-Client Invoice Methods (Password Protected)
  // ============================================

  // Look up client by email (any authenticated user)
  async lookupClientByEmail(email: string): Promise<ApiResponse<{ id: number; name: string; email: string }>> {
    return this.request(`/api/clients/lookup?email=${encodeURIComponent(email)}`)
  }

  // Get shipments for a specific client (any authenticated user)
  async getOtherClientShipments(clientId: string | number, page: number = 1): Promise<ApiResponse<any>> {
    return this.request(`/api/clients/${clientId}/shipments?page=${page}`)
  }

  // Get shipment items for a specific client's shipment (any authenticated user)
  async getOtherClientShipmentItems(clientId: string | number, shipmentId: string): Promise<ApiResponse<any>> {
    return this.request(`/api/clients/${clientId}/shipments/${shipmentId}/items`)
  }

  // Generate multi-client combined invoice (password protected)
  async generateMultiClientCombinedInvoice(data: {
    primaryClientId: string | number;
    secondaryClientId?: string | number;
    primaryShipments: any[];
    secondaryShipments?: any[];
    importerClientId: string | number;
    password: string;
    ltlType: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo';
    brokerType: 'clearit' | 'acb' | 'jas';
    fcInfo?: {
      fcId: string;
      fcAddress: {
        street: string;
        city: string;
        state: string;
        zip: string;
      };
    };
  }): Promise<ApiResponse<any>> {
    return this.request('/api/generate-multi-client-combined-invoice', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Legacy staff methods (kept for compatibility)
  async getStaffClientShipments(clientId: string | number, page: number = 1): Promise<ApiResponse<any>> {
    return this.getOtherClientShipments(clientId, page)
  }

  async getStaffClientShipmentItems(clientId: string | number, shipmentId: string): Promise<ApiResponse<any>> {
    return this.getOtherClientShipmentItems(clientId, shipmentId)
  }

  // === 3PL Methods ===

  async get3PLOrders(page: number = 1, status: string = 'awaiting_fulfillment'): Promise<ApiResponse<any>> {
    return this.request(`/api/3pl/orders?page=${page}&status=${encodeURIComponent(status)}`)
  }

  async get3PLOrderDetails(orderId: string | number): Promise<ApiResponse<any>> {
    return this.request(`/api/3pl/orders/${orderId}`)
  }

  async get3PLASINData(asin: string): Promise<ApiResponse<any>> {
    return this.request(`/api/3pl/asin-data/${asin}`)
  }

  async save3PLASINData(asin: string, data: any): Promise<ApiResponse<any>> {
    return this.request(`/api/3pl/asin-data/${asin}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async classify3PLHTS(description: string, countryOfOrigin?: string, asin?: string): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/classify-hts', {
      method: 'POST',
      body: JSON.stringify({ description, countryOfOrigin, asin }),
    })
  }

  async lookup3PLManufacturer(description: string, asin?: string, countryOfOrigin?: string): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/manufacturing-lookup', {
      method: 'POST',
      body: JSON.stringify({ description, asin, countryOfOrigin }),
    })
  }

  async upload3PLCatalogue(items: any[], password: string): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/upload-catalogue', {
      method: 'POST',
      body: JSON.stringify({ items, password, trustedDeviceId: trustedDevice.getId() }),
    })
  }

  async submit3PLToSSB(orderId: string | number, orderNumber: string, items: any[], customerAddress: any): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/submit-ssb', {
      method: 'POST',
      body: JSON.stringify({ orderId, orderNumber, items, customerAddress }),
    })
  }

  async check3PLSSBRate(data: {
    htsCode: string
    countryOfOrigin: string
    price: number
    description?: string
    asin?: string
    manufacturingAddress?: string
  }): Promise<ApiResponse<{
    dutyAmount: number
    dutyRate: string
    verified: boolean
    timestamp: string
  }>> {
    return this.request('/api/3pl/check-ssb-rate', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // === SSB Tracking & Sync ===

  async getSSBSubmissions(): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/ssb-submissions')
  }

  async getSSBTracking(veeqoOrderId: number): Promise<ApiResponse<any>> {
    return this.request(`/api/3pl/ssb-tracking/${veeqoOrderId}`)
  }

  async syncToVeeqo(veeqoOrderId: number, veeqoOrderNumber?: string, ssbUuid?: string, trackingNumber?: string, carrier?: string): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/sync-to-veeqo', {
      method: 'POST',
      body: JSON.stringify({ veeqoOrderId, veeqoOrderNumber, ssbUuid, trackingNumber, carrier })
    })
  }

  async linkSSBUuid(veeqoOrderId: number, veeqoOrderNumber: string, ssbUuid: string): Promise<ApiResponse<any>> {
    return this.request('/api/3pl/link-ssb', {
      method: 'POST',
      body: JSON.stringify({ veeqoOrderId, veeqoOrderNumber, ssbUuid })
    })
  }
}

export const apiService = new ApiService()
